import { Component, OnInit, ViewChild } from '@angular/core';
import { MobileCostingViewComponent } from './mobile-costing-view/mobile-costing-view.component';
import { BudgetFormPayload } from './mobile-costing-view/budget-item-offcanvas/budget-item-offcanvas.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  throwError,
  timeout,
} from 'rxjs';
import { ChartConfiguration, ChartData } from 'chart.js';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgChartsModule, BaseChartDirective } from 'ng2-charts';
import { AivorInsightsComponent } from './aivor-insights/aivor-insights.component';
import { environment } from '../../../../environments/environment';
import { StatisticCardsComponent } from '../../shared/reusables/statistic-cards/statistic-cards.component';
import { AuthService } from '../../shared/services';
import { jwtDecode } from 'jwt-decode';
import { InventoryService } from '../../../shared/services/inventory.service';
import { formatCurrency } from '@angular/common';
import { ToastService } from '../../shared/services/toast.service';

interface Budget {
  budgetID: string;
  budgetName: string;
  fiscalYear: number;
  budgetType: string;
  status: string;
  description: string;
  createdAt: Date;
  userIDCreated: string;
  totalActualCostFromInventory?: number;
  budgetCreationMonth?: string;
  lineItems?: BudgetLineItem[];
}

interface BudgetLineItem {
  budgetLineID: number;
  budgetID: number;
  fiscalPeriodID: number;
  costCenterID?: number | null;
  itemID?: number | null;
  costCategory: string;
  budgetAmount: number;
  notes?: string;
}

interface BudgetLineForm {
  fiscalPeriodID: number | null;
  costCenterID: number | null;
  itemID: number | null;
  costCategory: string;
  budgetAmount: number | null;
  notes: string;
}

interface CostDriver {
  id: number;
  name: string;
  description: string;
  impact: string;
  category: string;
}

export interface ItemDTO {
  itemID: number;
  itemCode: string;
  itemName: string;
  description?: string;
  itemTypeID: number;
  baseUOMID: number;
  lowStockThreshold?: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  targetSellingPrice?: number;
  targetProfitMarginPercent?: number;
  targetCost?: number;
  valuationMethodCode?: string;
  categoryID: number;
}

export interface fiscalPeriod {
  fiscalPeriodID: number;
  fiscalYear: number;
  periodNumber: number;
  periodType: string;
  startDate: Date;
  endDate: Date;
}

export interface inventoryTransaction {
  transactionID: number;
  itemID: number;
  warehouseID: number;
  transactionType: string;
  quantityChange: number;
  costPerUnit: number;
  transactionDate: Date;
  referenceType: string;
  referenceID: string;
  userID: number;
  notes: string;
  batchNumber: string;
  expiryDate: Date;
  referenceTypeCode: string;
}

export interface ActualBudgetCost {
  budgetID: string;
  budgetName: string;
  budgetCreationMonth: string;
  totalActualCostFromInventory: number;
}
interface BudgetItemSummary {
  itemID: number;
  itemName: string;
  lines: {
    name: string;
    budgetAmount: number;
  }[];
  total: number;
}

export interface ActualsApiResponse {
  actualBudgetCosts: ActualBudgetCost[];
  grandTotal: number;
  totalBudgets: number;
}
const tenantId = 1;

@Component({
  selector: 'app-costing-dashboard',
  standalone: true,
  imports: [CommonModule, AivorInsightsComponent, NgChartsModule, FormsModule, MobileCostingViewComponent],
  templateUrl: './costing-dashboard.component.html',
  styleUrls: ['./costing-dashboard.component.css'],
})
export class CostingDashboardComponent implements OnInit {
  public budgets: Budget[] = [];
  public budgetLineItems: BudgetLineItem[] = [];
  public grandTotalActualCosts = 0;
  public monthlyActualCosts: ActualBudgetCost[] = [];
  public itemtotal = 0;
  public budgetTotals: Record<string, number> = {};
  budgetTotalsMap: BudgetItemSummary[] = [];
  // Map of BudgetID -> actual cost (for current filters)
  actualCostByBudgetId: Record<string, number> = {};
  // Debug panel state and data
  showDebugPanel: boolean = false;
  actualsDebugByBudget: Record<string, {
    budgetID: string;
    budgetName?: string;
    linePeriodIds: string[];
    expandedPeriodIds: string[];
    allowedPeriodIds: string[];
    included: { period: string; amount: number }[];
    excluded: { period: string; amount: number }[];
    sum: number;
  } | undefined> = {};
  selectedYear: number = new Date().getFullYear();
  selectedBudgetType: string = 'monthly';
  selectedDepartment: string = 'All';
  selectedProductLine: string = 'All';
  dateRange: { start: Date | null; end: Date | null } = {
    start: null,
    end: null,
  };
  selectedMonth: number = new Date().getMonth();
  selectedQuarter: number = Math.floor(new Date().getMonth() / 3) + 1;
  availableMonths = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' }
  ];
  availableQuarters = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' }
  ];
  newBudgetLine: BudgetLineForm = {
    fiscalPeriodID: null,
    costCenterID: null,
    itemID: null,
    costCategory: '',
    budgetAmount: null,
    notes: '',
  };
  fiscalPeriods: any[] = [];
  allFiscalPeriods: any[] = [];
  costCenters: any[] = [];
  showAddLineForm = false;
  budgetCategoryMap: { [key: string]: string } = {};

  budgetForm: Budget = {
    budgetID: '',
    budgetName: '',
    fiscalYear: this.selectedYear,
    budgetType: 'Monthly',
    status: 'Draft',
    description: '',
    createdAt: new Date(),
    userIDCreated: 'currentUser',
  };

  animateClickFinished = false;
  variances: any[] = [];
  animateClickRaw = false;
  budgetCategories: any[] = [];
  costDrivers: CostDriver[] = [];
  costingData: any[] = [];
  inventory: any[] = [];

  availableYears: number[] = [];
  items: ItemDTO[] = [];
  itemsProducts: ItemDTO[] = [];
  inventoryTransactions: any[] = [];
  tenantName: any;
  fullName: any;

  // Delete confirmation modal state
  showDeleteBudgetModal: boolean = false;
  selectedBudgetToDelete: Budget | null = null;

  budgetAssignments: any[] = [];

  get filteredBudgets(): Budget[] {
    return (this.budgets || []).filter((b) => {
      // Filter by budget type if not 'all'
      const typeMatch =
        this.selectedBudgetType === 'all' ||
        this.normalizeBudgetType(b.budgetType) === this.selectedBudgetType;

      // Filter by date range if budget has line items with fiscal periods
      let dateMatch = true;
      if (
        this.dateRange.start &&
        this.dateRange.end &&
        b.lineItems &&
        b.lineItems.length > 0
      ) {
        // Include this budget only if ANY of its line items' fiscal periods overlap the range
        dateMatch = b.lineItems.some((item) => {
          const fiscalPeriod = this.getFiscalPeriodById(item.fiscalPeriodID);
          if (!fiscalPeriod) return false; // if not found, do not count as match
          const startRaw = fiscalPeriod?.startDate ?? fiscalPeriod?.StartDate;
          const endRaw = fiscalPeriod?.endDate ?? fiscalPeriod?.EndDate ?? startRaw;
          if (!startRaw) return false;
          const periodStart = new Date(startRaw);
          const periodEnd = new Date(endRaw);
          return (
            periodStart <= this.dateRange.end! &&
            periodEnd >= this.dateRange.start!
          );
        });
      }

      return typeMatch && dateMatch;
    });
  }

  // Pagination helpers for the chart
  get totalPages(): number {
    const total = this.filteredBudgets.length;
    return total > 0 ? Math.ceil(total / this.pageSize) : 1;
  }

  get paginatedBudgets(): Budget[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredBudgets.slice(start, end);
  }

  get pageStartDisplay(): number {
    const total = this.filteredBudgets.length;
    if (total === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEndDisplay(): number {
    const total = this.filteredBudgets.length;
    return Math.min(this.currentPage * this.pageSize, total);
  }

  // Calculate total budget amount for all budgets
  public calculateCurrentMonthTotalBudget(): number {
    const list = this.filteredBudgets;
    if (!list) return 0;
    return list.reduce((total, budget) => total + this.calculateTotalLineItemAmount(budget), 0);
  }

  // Helper method to get fiscal period by ID (searches the unfiltered list)
  private getFiscalPeriodById(fiscalPeriodID: number | null): any {
    if (!fiscalPeriodID) return null;
    return (this.allFiscalPeriods || []).find(
      (fp: any) => fp?.fiscalPeriodID === fiscalPeriodID
    );
  }

  // Get total actual costs for the currently filtered budgets
  public getCurrentMonthTotalActualCosts(): number {
    // Use the grand total computed from raw ActualBudgetCost rows for the current fiscal period filter
    return Number(this.grandTotalActualCosts || 0);
  }

  showBudgetModal = false;
  editMode = false;
  isLoading = false;
  isProcessing = true;
  // Pagination state for Budget vs Actual chart
  currentPage: number = 1;
  pageSize: number = 10;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  // Pagination controls
  goToPrevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.refreshChartData();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.refreshChartData();
    }
  }

  private clampCurrentPage(): void {
    const pages = this.totalPages;
    if (!pages || pages < 1) {
      this.currentPage = 1;
    } else if (this.currentPage > pages) {
      this.currentPage = pages;
    } else if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  onPageSizeChange(size: number): void {
    // Ensure numeric and reasonable bounds
    const s = Number(size) || 10;
    this.pageSize = s < 1 ? 1 : s;
    this.currentPage = 1;
    this.refreshChartData();
  }

  budgetVsActualChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      { label: 'Budgeted', data: [], backgroundColor: '#4e73df' },
      { label: 'Actual', data: [], backgroundColor: '#1cc88a' },
    ],
  };

  chartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: false },
      y: {
        type: 'linear',
        suggestedMin: 0,
        suggestedMax: Math.max(...this.budgetVsActualChartData.datasets[0].data as number[]) * 1.2,
        ticks: {
          callback: (value) => `R ${value}`,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const amount = context?.formattedValue;
            const index = context?.dataIndex ?? 0;
            const budget = (this.paginatedBudgets || [])[index];
            const type = budget ? this.normalizeBudgetType(budget.budgetType) : undefined;
            const typeLabel = type ? this.getBudgetTypeLabel(type) : '';
            const name = budget?.budgetName ?? context?.label ?? '';
            const periodText = budget ? this.getBudgetDateLabel(budget) : this.formatRangeShort();
            const itemName = type === 'per-item' ? (this.getFirstItemName(budget) || 'Item') : '';
            const budgetLine = type === 'per-item' && itemName
              ? ` - ${itemName} for ${periodText}`
              : ` for ${periodText}`;
            const desc = (budget?.description ?? budget?.description) as string | undefined;

            const lines: string[] = [];
            if (amount) {
              const formattedAmount = amount.toLocaleString('en-ZA', {
                style: 'currency',
                currency: 'ZAR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              lines.push(`Amount: R ${formattedAmount}`);
            }

            if (typeLabel) lines.push(typeLabel + budgetLine);

            if (desc) {
              lines.push(`Description: ${desc}`);
            }
            return lines;
          },
        },
      },
    },
  };
  userRole: string | null = null;

  // Normalize backend responses to handle both camelCase and PascalCase keys
  private normalizeActuals(res: any): ActualsApiResponse {
    const list = (res?.actualBudgetCosts ?? res?.ActualBudgetCosts ?? []) as any[];
    const normalizedCosts: ActualBudgetCost[] = (Array.isArray(list) ? list : []).map((c: any) => ({
      budgetID: String(c?.budgetID ?? c?.BudgetID ?? ''),
      budgetName: c?.budgetName ?? c?.BudgetName ?? '',
      budgetCreationMonth: c?.budgetCreationMonth ?? c?.BudgetCreationMonth ?? c?.period ?? c?.Period ?? '',
      totalActualCostFromInventory: Number(c?.totalActualCostFromInventory ?? c?.TotalActualCostFromInventory ?? 0),
    }));

    return {
      actualBudgetCosts: normalizedCosts,
      grandTotal: Number(res?.grandTotal ?? res?.GrandTotal ?? 0),
      totalBudgets: Number(res?.totalBudgets ?? res?.TotalBudgets ?? normalizedCosts.length ?? 0),
    } as ActualsApiResponse;
  }
// Basic validation for budget lines used to disable Save button in the modal
public hasInvalidBudgetLines(): boolean {
  // If there are no overlapping fiscal periods, treat as invalid to block save
  if (!Array.isArray(this.fiscalPeriods) || this.fiscalPeriods.length === 0) {
    return true;
  }
  const lines = this.budgetForm?.lineItems || [];
  if (!lines.length) return true;
  // For per-item budgets, require itemID
  const isPerItem = this.normalizeBudgetType(this.budgetForm?.budgetType) === 'per-item';
  return lines.some((line) => {
    const missingItem = isPerItem && !line.itemID;
    const invalidAmount = line.budgetAmount == null || isNaN(Number(line.budgetAmount)) || Number(line.budgetAmount) < 0;
    const invalidPeriod = !line.fiscalPeriodID || !this.fiscalPeriods.find(p => Number(p?.fiscalPeriodID) === Number(line.fiscalPeriodID));
    return missingItem || invalidAmount || invalidPeriod;
  });
}

  // Normalize budget type values from backend/user input to a canonical set
  private normalizeBudgetType(type?: string): 'monthly' | 'quarterly' | 'annually' | 'per-item' {
    const t = (type || '')
      .toLowerCase()
      .trim()
      .replace(/[_\s]+/g, '-'); // convert spaces/underscores to hyphen

    if (t === 'monthly' || t === 'month') return 'monthly';
    if (t === 'quarterly' || t === 'quarter') return 'quarterly';
    if (t === 'annually' || t === 'annual' || t === 'yearly' || t === 'year') return 'annually';
    // Handle common variants like "per item", "peritem", "per-item"
    if (t === 'per-item' || t === 'peritem' || t === 'per-items') return 'per-item';
    // Default safe fallback
    return 'monthly';
  }

  // Resolve a friendly item name for per-item budgets (first matching item on the first line with an itemID)
  private getFirstItemName(budget?: Budget): string {
    if (!budget || !Array.isArray(budget.lineItems) || !Array.isArray(this.items)) return '';
    const withItem = budget.lineItems.find(li => !!li.itemID);
    if (!withItem || !withItem.itemID) return '';
    const item = this.items.find(i => i.itemID === withItem.itemID);
    return item?.itemName || '';
  }

  // Format the currently selected date range into a short, human-friendly string
  private formatRangeShort(): string {
    const { start, end } = this.dateRange || {} as any;
    if (!start && !end) return 'All dates';
    const fmt = (d: Date, opts?: Intl.DateTimeFormatOptions) =>
      new Date(d).toLocaleDateString('en-ZA', opts || { day: '2-digit', month: 'short', year: 'numeric' });

    if (start && end) {
      // If same month & year, show e.g. "Aug 2025"
      if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
        return fmt(start, { month: 'short', year: 'numeric' });
      }
      // Otherwise show full range
      return `${fmt(start)} - ${fmt(end)}`;
    }
    if (start) return `From ${fmt(start)}`;
    if (end) return `Up to ${fmt(end)}`;
    return 'All dates';
  }

  // Friendly label for budget type
  private getBudgetTypeLabel(type: 'monthly' | 'quarterly' | 'annually' | 'per-item'): string {
    switch (type) {
      case 'monthly':
        return 'Monthly';
      case 'quarterly':
        return 'Quarterly';
      case 'annually':
        return 'Annually';
      case 'per-item':
        return 'Per Item';
      default:
        return '';
    }
  }

  // Compute a date label for the budget itself (not the filter), using its line items' fiscal periods
  private getBudgetDateLabel(budget: Budget): string {
    if (!budget) return this.formatRangeShort();
    const type = this.normalizeBudgetType(budget.budgetType);

    // Helper to format month-year
    const fmtMY = (d: Date) => new Date(d).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });

    // Gather fiscal period bounds from allFiscalPeriods (unfiltered source of truth)
    const periods = (budget.lineItems || [])
      .map(li => this.getFiscalPeriodById(li.fiscalPeriodID))
      .filter((p: any) => !!p);

    if (type === 'annually') {
      // Prefer explicit fiscalYear; else derive from first period start; else from createdAt
      const year = budget.fiscalYear
        || (periods[0]?.startDate ? new Date(periods[0].startDate).getFullYear() : undefined)
        || (periods[0]?.StartDate ? new Date(periods[0].StartDate).getFullYear() : undefined)
        || (budget.createdAt ? new Date(budget.createdAt).getFullYear() : undefined);
      return year ? String(year) : 'Annual';
    }

    if (periods.length === 0) {
      // Fallbacks if no matching periods found
      if (type === 'monthly') {
        return budget.budgetCreationMonth || fmtMY(new Date());
      }
      if (type === 'quarterly') {
        const y = budget.fiscalYear || new Date().getFullYear();
        return `Q1 ${y}`;
      }
      // per-item or others
      return this.formatRangeShort();
    }

    // Compute min start and max end
    const starts: Date[] = periods.map((p: any) => new Date(p.startDate ?? p.StartDate));
    const ends: Date[] = periods.map((p: any) => new Date(p.endDate ?? p.EndDate ?? (p.startDate ?? p.StartDate)));
    const minStart = new Date(Math.min.apply(null, starts.map(d => d.getTime())));
    const maxEnd = new Date(Math.max.apply(null, ends.map(d => d.getTime())));

    if (type === 'monthly') {
      // If all in the same month-year, show that; else show condensed range of months
      const uniqueMY = new Set(periods.map((p: any) => {
        const d = new Date(p.startDate ?? p.StartDate);
        return `${d.getFullYear()}-${d.getMonth()}`;
      }));
      if (uniqueMY.size === 1) return fmtMY(minStart);
      return `${fmtMY(minStart)} - ${fmtMY(maxEnd)}`;
    }

    if (type === 'quarterly') {
      // Derive quarter from minStart
      const m = minStart.getMonth();
      const q = Math.floor(m / 3) + 1; // 0..11 -> 1..4
      return `Q${q} ${minStart.getFullYear()}`;
    }

    // per-item: show a concise range across the budget's periods
    const sameMonthYear = (minStart.getFullYear() === maxEnd.getFullYear()) && (minStart.getMonth() === maxEnd.getMonth());
    if (sameMonthYear) return fmtMY(minStart);
    return `${fmtMY(minStart)} - ${fmtMY(maxEnd)}`;
  }

  constructor(private http: HttpClient,
    public authService: AuthService,
    private inventoryService: InventoryService,
    private toast: ToastService
  ) {
    const currentYear = new Date().getFullYear();
    this.availableYears = [currentYear - 1, currentYear, currentYear + 1];
    this.selectedYear = currentYear;
    // Default to monthly view: set range to the current month
    this.dateRange = {
      start: new Date(currentYear, this.selectedMonth, 1),
      end: new Date(currentYear, this.selectedMonth + 1, 0),
    };
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString); // Converts the date string (e.g., 'YYYY-MM-DD') to a Date object

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  // Build a safe date string for API query params in 'YYYY-MM-DD' using local date parts
  private formatDateParam(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Helper to bind Date to <input type="date"> safely
  dateToInput(date: Date | null): string {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Parse from <input type="date"> and refresh
  onStartDateChange(value: string): void {
    this.dateRange.start = value ? new Date(`${value}T00:00:00`) : null;
    if (this.dateRange.start && this.dateRange.end && this.dateRange.start > this.dateRange.end) {
      this.dateRange.end = new Date(this.dateRange.start);
    }
    // Refresh fiscal periods to reflect the selected date range
    this.loadFiscalPeriods();
    this.currentPage = 1; // reset pagination on filter change
    this.loadAllBudgetData();
  }

  onEndDateChange(value: string): void {
    this.dateRange.end = value ? new Date(`${value}T00:00:00`) : null;
    if (this.dateRange.start && this.dateRange.end && this.dateRange.start > this.dateRange.end) {
      this.dateRange.start = new Date(this.dateRange.end);
    }
    // Refresh fiscal periods to reflect the selected date range
    this.loadFiscalPeriods();
    this.currentPage = 1; // reset pagination on filter change
    this.loadAllBudgetData();
  }

  onBudgetTypeChange(type: string): void {
    this.selectedBudgetType = type;
    this.updateDateRangeForType(type);
    this.currentPage = 1; // reset pagination on filter change
    this.loadAllBudgetData();
  }

  updateDateRangeForType(type: string): void {
    const year = this.selectedYear;
    // If user selects 'All types', force full-year date range and return early
    if ((type || '').toLowerCase() === 'all') {
      this.dateRange = {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
      };
      this.loadFiscalPeriods();
      return;
    }
    const normalizedType = this.normalizeBudgetType(type);

    switch(normalizedType) {
      case 'monthly':
        // Set date range to selected month
        const monthStart = new Date(year, this.selectedMonth, 1);
        const monthEnd = new Date(year, this.selectedMonth + 1, 0);
        this.dateRange = { start: monthStart, end: monthEnd };
        break;

      case 'quarterly':
        // Set date range to selected quarter
        const quarterStart = new Date(year, (this.selectedQuarter - 1) * 3, 1);
        const quarterEnd = new Date(year, this.selectedQuarter * 3, 0);
        this.dateRange = { start: quarterStart, end: quarterEnd };
        break;

      case 'annually':
        // Set date range to selected year
        this.dateRange = {
          start: new Date(year, 0, 1),
          end: new Date(year, 11, 31)
        };
        break;

      default:
        // For 'per-item', keep current date range
        break;
    }

    this.loadFiscalPeriods();
  }

  onMonthChange(month: number): void {
    this.selectedMonth = month;
    if (this.selectedBudgetType === 'monthly') {
      this.updateDateRangeForType('monthly');
      this.currentPage = 1; // reset pagination on filter change
      this.loadAllBudgetData();
    }
  }

  onQuarterChange(quarter: number): void {
    this.selectedQuarter = quarter;
    if (this.selectedBudgetType === 'quarterly') {
      this.updateDateRangeForType('quarterly');
      this.currentPage = 1; // reset pagination on filter change
      this.loadAllBudgetData();
    }
  }

  onYearChangeForType(year: number): void {
    this.selectedYear = year;
    if (this.selectedBudgetType === 'annually') {
      this.updateDateRangeForType('annually');
      this.currentPage = 1; // reset pagination on filter change
      this.loadAllBudgetData();
    }
  }

  resetDateRange(): void {
    const currentYear = new Date().getFullYear();
    this.selectedYear = currentYear;
    this.selectedMonth = new Date().getMonth();
    this.selectedQuarter = Math.floor(new Date().getMonth() / 3) + 1;
    this.dateRange = {
      start: new Date(currentYear, 0, 1),
      end: new Date(currentYear, 11, 31),
    };
    // Reset fiscal periods to the full current year
    this.loadFiscalPeriods(currentYear);
    this.currentPage = 1; // reset pagination on filter change
    this.loadAllBudgetData();
  }

  ngOnInit(): void {
    const JWT = localStorage.getItem('authToken');
    const decodedToken: any = jwtDecode(JWT!);
    this.tenantName = decodedToken.TenantName;
    this.fullName = decodedToken.FullName;
    this.userRole = this.authService.getUserRole();
// $&
    // Ensure fiscal periods are available before first budget load so filtering works and chart renders
    this.loadFiscalPeriods(undefined, () => {
      this.loadAllBudgetData();
    });
    this.loadItems();
    this.loadCategories();
  }

  loadFiscalPeriods(year?: number, afterLoad?: () => void): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http
      .get<any[]>(`${environment.apiUrl}/api/FiscalPeriods/GetAll`, { headers })
      .pipe(timeout(15000))
      .subscribe({
        next: (periods) => {
          // Keep an unfiltered copy for lookups (e.g., filtering budgets regardless of current dropdown filtering)
          this.allFiscalPeriods = Array.isArray(periods) ? periods : [];
          const targetYear = year ?? this.selectedYear ?? new Date().getFullYear();
          // Determine filtering strategy: by overlapping date range if set, otherwise by year
          const rangeStart: Date | null = this.dateRange?.start ? new Date(this.dateRange.start) : null;
          const rangeEnd: Date | null = this.dateRange?.end ? new Date(this.dateRange.end) : null;

          const filtered = (periods || []).filter((period: any) => {
            // Support both camelCase and PascalCase from backend
            const startRaw = period?.startDate ?? period?.StartDate;
            const endRaw = period?.endDate ?? period?.EndDate ?? startRaw;
            if (!startRaw) return false;
            const pStart = new Date(startRaw);
            const pEnd = new Date(endRaw);

            if (rangeStart && rangeEnd) {
              // Include fiscal periods that overlap the selected date range
              return pEnd >= rangeStart && pStart <= rangeEnd;
            }
            // Fallback: filter by target year
            return pStart.getFullYear() === targetYear;
          });

          // Sort by start date ascending for a stable dropdown order
          this.fiscalPeriods = filtered.sort((a: any, b: any) => {
            const aStart = new Date(a?.startDate ?? a?.StartDate).getTime();
            const bStart = new Date(b?.startDate ?? b?.StartDate).getTime();
            return aStart - bStart;
          });
          // Ensure any existing budget line selections remain valid after refresh
          this.ensureValidLinePeriods();
          if (afterLoad) afterLoad();
        },
        error: (error) => {
          console.error('Error loading fiscal periods:', error);
          if (afterLoad) afterLoad();
        },
      });
  }

  // Ensure existing line items have valid fiscalPeriodIDs based on the current filtered fiscalPeriods
  private ensureValidLinePeriods(): void {
    const lines = this.budgetForm?.lineItems || [];
    if (!Array.isArray(lines) || lines.length === 0) return;

    const availableIds = new Set(
      (this.fiscalPeriods || []).map((p: any) => p?.fiscalPeriodID)
    );

    lines.forEach((line) => {
      if (!availableIds.has(line.fiscalPeriodID)) {
        line.fiscalPeriodID =
          this.fiscalPeriods && this.fiscalPeriods.length > 0
            ? this.fiscalPeriods[0].fiscalPeriodID
            : 0;
      }
    });
  }

  loadCategories(): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    this.http
      .get<any[]>(`${environment.apiUrl}/api/Category/GetAll`, { headers })
      .pipe(timeout(15000))
      .subscribe({
        next: (Categories) => {
          this.budgetCategories = Categories;
        },
        error: (error) => {
          console.error('Error loading categories:', error);
        },
      });

  }

  onModalBudgetTypeChange(type: string): void {
    // Update date range based on budget type selection in modal
    const normalizedType = this.normalizeBudgetType(type);
    this.updateDateRangeForType(normalizedType);
  }

  loadBudgetLineItems(budgetId: string): void {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http
      .get<BudgetLineItem[]>(`${environment.apiUrl}/api/BudgetLines/GetByBudgetId?budgetId=${budgetId}`, { headers })
      .pipe(timeout(15000))
      .subscribe({
        next: (lineItems) => {
          this.budgetLineItems = lineItems || [];
          this.budgetForm.lineItems = lineItems || [];
        },
        error: (error) => {
          console.error('Error loading budget line items:', error);
          this.budgetLineItems = [];
          this.budgetForm.lineItems = [];
        },
      });
  }

  openBudgetModal(budget: Budget | null = null): void {
    if (budget) {
      this.budgetForm = { ...budget };
      this.loadBudgetLineItems(budget.budgetID);
    } else {
      this.budgetForm = {
        budgetID: '',
        budgetName: '',
        fiscalYear: this.selectedYear,
        budgetType: 'Monthly',
        status: 'Draft',
        description: '',
        createdAt: new Date(),
        userIDCreated: 'currentUser',
      };
      this.budgetLineItems = [];
    }

    this.showBudgetModal = true;
    // Ensure fiscal periods are up to date with the current date range when opening the modal
    this.loadFiscalPeriods();
  }
  closeBudgetModal(): void {
    this.showBudgetModal = false;
    this.budgetForm = {
      budgetID: '',
      budgetName: '',
      fiscalYear: this.selectedYear,
      budgetType: 'Monthly',
      status: 'Draft',
      description: '',
      createdAt: new Date(),
      userIDCreated: 'currentUser',
    };
    this.budgetLineItems = [];
  }

  onMobileSaveBudget(payload: BudgetFormPayload): void {
    this.budgetForm = {
      budgetID: payload.budget.budgetID,
      budgetName: payload.budget.budgetName,
      fiscalYear: payload.budget.fiscalYear,
      budgetType: payload.budget.budgetType,
      status: this.budgetForm.status || 'Draft',
      description: payload.budget.description,
      createdAt: new Date(),
      userIDCreated: 'currentUser',
      lineItems: payload.lineItems as any,
    };
    this.budgetLineItems = payload.lineItems as any;
    this.saveBudget();
  }

  openDeleteBudgetModal(budget: Budget): void {
    this.selectedBudgetToDelete = budget;
    this.showDeleteBudgetModal = true;
  }

  closeDeleteBudgetModal(): void {
    this.showDeleteBudgetModal = false;
    this.selectedBudgetToDelete = null;
  }

  confirmDeleteBudget(): void {
    if (!this.selectedBudgetToDelete) return;
    const id = this.selectedBudgetToDelete.budgetID;
    this.closeDeleteBudgetModal();
    this.deleteBudget(id);
  }

  deleteBudget(budgetId: string): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
// $&
      return;
    }

    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    // First, find the budget to get its line items
    this.http
      .get<Budget>(`${environment.apiUrl}/api/Budgets/GetById?id=${budgetId}`, {
        headers,
      })
      .subscribe({
        next: (budget) => {
          if (budget.lineItems && budget.lineItems.length > 0) {
            // Delete all budget lines first
            const deleteLineItems$ = budget.lineItems.map((line) =>
              this.http.delete(
                `${environment.apiUrl}/api/BudgetLines/Delete?BudgetLinesId=${line.budgetLineID}`,
                {
                  headers,
                  responseType: 'text', // Handle non-JSON response
                }
              )
            );

            forkJoin(deleteLineItems$).subscribe({
              next: () => {
                // After all line items are deleted, delete the budget
                this.deleteBudgetAfterLines(budgetId, headers);
                this.deleteActualBudgetCost(budgetId, headers);
              },
              error: (error) => {
                console.error('Error deleting budget lines:', error);
                // Still try to delete the budget even if some line items couldn't be deleted
                this.deleteBudgetAfterLines(budgetId, headers);
                this.deleteActualBudgetCost(budgetId, headers);
              },
            });
          } else {
            // If no line items, just delete the budget
            this.deleteBudgetAfterLines(budgetId, headers);
            this.deleteActualBudgetCost(budgetId, headers);
          }
        },
        error: (error) => {
          console.error('Error getting budget details:', error);
          // If we can't get the budget details, still try to delete it
          this.deleteBudgetAfterLines(budgetId, headers);
          this.deleteActualBudgetCost(budgetId, headers);
        },
      });
  }

  private deleteBudgetAfterLines(budgetId: string, headers: HttpHeaders): void {
    this.http
      .delete(
        `${environment.apiUrl}/api/Budgets/Delete?BudgetsId=${budgetId}`,
        {
          headers,
          responseType: 'text', // Handle non-JSON response
        }
      )
      .subscribe({
        next: () => {
          this.loadAllBudgetData();
        },
        error: (error) => {
          console.error('Error deleting budget:', error);
          // Still refresh the data to show current state
          this.loadAllBudgetData();
        },
      });
  }



  private deleteActualBudgetCost(budgetId: string, headers: HttpHeaders): void {
    this.http
      .delete(
        `${environment.apiUrl}/api/ActualBudgetCost/Delete?ActualBudgetCostId=${budgetId}`,
        {
          headers,
          responseType: 'text', // Handle non-JSON response
        }
      )
      .subscribe({
        next: () => {
          this.loadAllBudgetData();
        },
        error: (error) => {
          console.error('Error deleting budget:', error);
          // Still refresh the data to show current state
          this.loadAllBudgetData();
        },
      });
  }

  private updateCharts(): void {
    const actualAmount = this.calculateGrandTotalBudgetAmount();
// $&
  }

  private refreshChartData(): void {
    this.clampCurrentPage();
    const list = this.paginatedBudgets;
    const labels = list.map((b) => b.budgetName || '');
    const budgetedData = list.map((budget) =>
      (budget.lineItems || []).reduce(
        (sum, item) => sum + (Number(item.budgetAmount) || 0),
        0
      )
    );
    const actualData = list.map((budget) => Number(budget.totalActualCostFromInventory) || 0);

    // If any non-positive values exist, switch to linear scale to avoid log-scale rendering issues
    const hasNonPositive = [...budgetedData, ...actualData].some((v) => v <= 0);
    const newY: any = {
      ...(this.chartOptions?.scales?.['y'] as any),
      type: hasNonPositive ? 'linear' : 'logarithmic',
    };
    if (hasNonPositive) {
      newY.beginAtZero = true;
      newY.min = 0;
    } else {
      // Ensure no linear-only settings leak into log scale
      delete newY.beginAtZero;
      delete newY.min;
    }
    this.chartOptions = {
      ...this.chartOptions,
      scales: {
        ...(this.chartOptions?.scales || {}),
        y: newY as any,
      },
    };

    this.budgetVsActualChartData = {
      ...this.budgetVsActualChartData,
      labels,
      datasets: [
        {
          label: 'Budgeted Amount',
          data: budgetedData,
          backgroundColor: '#4e73df',
          borderRadius: 4,
        },
        {
          label: 'Actual Amount',
          data: actualData,
          backgroundColor: '#1cc88a',
          borderRadius: 4,
        },
      ],
    };
    // Force an explicit chart update to ensure render on initial load
    setTimeout(() => this.chart?.update() , 0);
    this.isProcessing = false;
  }

  // Add a new empty budget line
  public addNewBudgetLine(): void {
    if (!this.fiscalPeriods || this.fiscalPeriods.length === 0) {
      alert('No fiscal periods available for the selected date range. Adjust the date range to add lines.');
      return;
    }
    if (!this.budgetForm.lineItems) {
      this.budgetForm.lineItems = [];
    }

    const newLine: BudgetLineItem = {
      budgetLineID: 0,
      budgetID: this.budgetForm.budgetID
        ? parseInt(this.budgetForm.budgetID)
        : 0,
      fiscalPeriodID:
        this.fiscalPeriods.length > 0
          ? this.fiscalPeriods[0].fiscalPeriodID
          : 0,
      costCenterID: null,
      itemID: null,
      costCategory: '',
      budgetAmount: 0,
      notes: '',
    };

    this.budgetForm.lineItems.push(newLine);
  }

  // Remove a budget line by index
  public removeBudgetLine(index: number): void {
    if (this.budgetForm.lineItems && this.budgetForm.lineItems.length > index) {
      this.budgetForm.lineItems.splice(index, 1);
    }
  }

  // Save budget and its lines
  public saveBudget(): void {
    // Prevent saving if there are no fiscal periods in the current range
    if (!Array.isArray(this.fiscalPeriods) || this.fiscalPeriods.length === 0) {
      alert('Cannot save: no fiscal periods available for the selected date range. Adjust the date range and try again.');
      return;
    }

    // Basic validation for line items
    if (this.hasInvalidBudgetLines()) {
      alert('Please ensure all budget lines have a valid fiscal period, non-negative amount, and (for per-item budgets) an item selected.');
      return;
    }
    // Validation for 'per-item' budgets
    if (this.budgetForm.budgetType === 'per-item') {
      const invalidLines = this.budgetForm.lineItems?.some(
        (line) => !line.itemID
      );
      if (invalidLines) {
        alert(
          'For per-item budgets, each budget line must have an item selected.'
        );
        return;
      }
    }

    if (
      !this.budgetForm.budgetName ||
      !this.budgetForm.fiscalYear ||
      !this.budgetForm.lineItems?.length
    ) {
      alert(
        'Please fill in all required fields and add at least one budget line'
      );
      return;
    }

    this.isLoading = true;
    const token = localStorage.getItem('authToken');

    if (!token) {
// $&
      this.isLoading = false;
      return;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    // First create the budget
    const budgetData = {
      ...this.budgetForm,
      budgetID: this.budgetForm.budgetID
        ? parseInt(this.budgetForm.budgetID)
        : 0,
      userIDCreated: 1, // Replace with actual user ID from auth
      createdAt: new Date().toISOString(),
      lineItems: undefined, // Don't send line items with the budget
    };

    const apiCall = this.budgetForm.budgetID
      ? this.http.put<Budget>(
          `${environment.apiUrl}/api/Budgets/Update?BudgetsId=${this.budgetForm.budgetID}`,
          budgetData,
          { headers }
        )
      : this.http.post<Budget>(
          `${environment.apiUrl}/api/Budgets/Add`,
          budgetData,
          { headers }
        );

    apiCall.subscribe({
      next: (budgetResponse) => {
        if (budgetResponse) {
          // Save each budget line
          const lineItemCalls = (this.budgetForm.lineItems || []).map(
            (line) => {
              // Ensure numeric fields are properly converted
              const lineData: BudgetLineItem = {
                ...line,
                budgetID: Number(budgetResponse.budgetID),
                budgetLineID: line.budgetLineID ? Number(line.budgetLineID) : 0,
                fiscalPeriodID: Number(line.fiscalPeriodID) || 0,
                costCenterID: line.costCenterID
                  ? Number(line.costCenterID)
                  : null,
                itemID: line.itemID ? Number(line.itemID) : null,
                costCategory: line.costCategory || 'Uncategorized',
                budgetAmount: Number(line.budgetAmount) || 0,
                notes: line.notes || '',
              };

              return line.budgetLineID
                ? this.http.put<BudgetLineItem>(
                    `${environment.apiUrl}/api/BudgetLines/Update?BudgetLinesId=${lineData.budgetLineID}`,
                    lineData,
                    { headers }
                  )
                : this.http.post<BudgetLineItem>(
                    `${environment.apiUrl}/api/BudgetLines/Add`,
                    lineData,
                    { headers }
                  );
            }
          );

          if (lineItemCalls.length > 0) {
            forkJoin(lineItemCalls).subscribe({
              next: () => {
                this.toast.success(`Budget ${this.editMode ? 'updated' : 'saved'} successfully`, 3000);
                this.showBudgetModal = false;
                this.loadAllBudgetData();
                this.resetBudgetForm();
                this.isLoading = false;
              },
              error: (error) => {
                console.error('Error saving budget lines:', error);
                this.isLoading = false;
                alert(
                  'Budget was saved, but there was an error saving some budget lines.'
                );
              },
            });
          } else {
            this.toast.success(`Budget ${this.editMode ? 'updated' : 'saved'} successfully`, 3000);
            this.showBudgetModal = false;
            this.loadAllBudgetData();
            this.resetBudgetForm();
            this.isLoading = false;
          }
        }
      },
      error: (error) => {
        console.error('Error saving budget:', error);
        this.isLoading = false;
        alert('Error saving budget. Please try again.');
      },
    });
  }

  // Reset the budget form
  private resetBudgetForm(): void {
    this.budgetForm = {
      budgetID: '',
      budgetName: '',
      fiscalYear: this.selectedYear,
      budgetType: 'Monthly',
      status: 'Draft',
      description: '',
      createdAt: new Date(),
      userIDCreated: '1',
      lineItems: [],
    };
    this.editMode = false;
  }

  public loadAllBudgetData(): void {
    this.isLoading = true;
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    const budgets$ = this.http
      .get<Budget[]>(`${environment.apiUrl}/api/Budgets/GetAll`, { headers })
      .pipe(
        timeout(15000),
        catchError((error) => {
          console.error('Error loading budgets:', error);
          return of([] as Budget[]);
        })
      );

    const lineItems$ = this.http
      .get<BudgetLineItem[]>(`${environment.apiUrl}/api/BudgetLines/GetAll`, { headers })
      .pipe(
        timeout(15000),
        catchError((error) => {
          console.error('Error loading line items:', error);
          return of([] as BudgetLineItem[]);
        })
      );

    const actuals$ = this.http
      .get<any[]>(`${environment.apiUrl}/api/ActualBudgetCost/GetAll`, { headers })
      .pipe(
        timeout(15000),
        catchError((error) => {
          console.error('Error loading actual budget costs:', error);
          return of([] as any[]);
        })
      );

    forkJoin([budgets$, lineItems$, actuals$])
      .pipe(
        map(([budgets, lineItems, actuals]) => {
          // Build quick lookup of line items per budget (keys normalized to string)
          const lineItemsByBudget: { [key: string]: BudgetLineItem[] } = {};
          (lineItems || []).forEach((li) => {
            const key = String(li?.budgetID);
            if (!lineItemsByBudget[key]) lineItemsByBudget[key] = [];
            lineItemsByBudget[key].push(li);
          });

          // Allowed fiscal period IDs based on current date range filter
          const allowedPeriodIds = new Set(
            (this.fiscalPeriods || []).map((p: any) => String(p?.fiscalPeriodID))
          );

          // Build full fiscal period lookup by ID (from allFiscalPeriods)
          const allPeriodsById = new Map<string, any>();
          (this.allFiscalPeriods || []).forEach((p: any) => {
            const id = String(p?.fiscalPeriodID ?? p?.FiscalPeriodID ?? '');
            if (id) allPeriodsById.set(id, p);
          });

          // Normalize actuals payload (handle possible casing differences and sum duplicate periods)
          const actualsByKey = new Map<string, number>();
          (Array.isArray(actuals) ? actuals : []).forEach((a: any) => {
            const budgetID = String(a?.budgetID ?? a?.BudgetID ?? '');
            const period = String(a?.period ?? a?.Period ?? '');
            const amount = Number(a?.totalActualCostFromInventory ?? a?.TotalActualCostFromInventory ?? 0);

            if (budgetID && period) {
              const key = `${budgetID}_${period}`;
              actualsByKey.set(key, (actualsByKey.get(key) || 0) + amount);
            }
          });

          // Convert to array of normalized actuals
          const actualsNormalized = Array.from(actualsByKey.entries()).map(([key, amount]) => {
            const [budgetID, period] = key.split('_');
            return {
              budgetID,
              period,
              totalActualCostFromInventory: amount
            };
          });

          // Compute actual cost sums per budget for relevant fiscal periods
          const actualCostByBudgetId: Record<string, number> = {};
          const debugMap: Record<string, {
            budgetID: string;
            budgetName?: string;
            linePeriodIds: string[];
            expandedPeriodIds: string[];
            allowedPeriodIds: string[];
            included: { period: string; amount: number }[];
            excluded: { period: string; amount: number }[];
            sum: number;
          } | undefined> = {};

          const combinedBudgets = (budgets || []).map((budget) => {
            const bIdStr = String((budget as any).budgetID);
            const items = lineItemsByBudget[bIdStr] || [];

            // Fiscal periods referenced by this budget's lines
            const itemPeriodIds = new Set(items.map((li) => String(li.fiscalPeriodID)));
            // Expand periods based on budget type (quarterly => months in quarter, annually => all months in year)
            const expandedPeriodIds = new Set<string>();
            const budgetTypeNorm = this.normalizeBudgetType((budget as any)?.budgetType);
            for (const id of itemPeriodIds) {
              const fp = allPeriodsById.get(id);
              if (!fp) continue;
              let fy = Number(fp?.fiscalYear ?? fp?.FiscalYear);
              let monthNum = Number(fp?.periodNumber ?? fp?.PeriodNumber);
              if (Number.isNaN(fy) || Number.isNaN(monthNum)) {
                const dStr = fp?.startDate ?? fp?.StartDate;
                if (dStr) {
                  const d = new Date(dStr);
                  if (!Number.isNaN(d.getTime())) {
                    fy = d.getFullYear();
                    monthNum = d.getMonth() + 1; // 1-12
                  }
                }
              }

              if (budgetTypeNorm === 'quarterly') {
                if (!Number.isNaN(fy) && !Number.isNaN(monthNum)) {
                  const quarter = Math.floor((monthNum - 1) / 3) + 1;
                  const startM = 3 * (quarter - 1) + 1;
                  const endM = 3 * quarter;
                  for (const ap of (this.allFiscalPeriods || [])) {
                    const apType = String(ap?.periodType ?? ap?.PeriodType ?? '').toLowerCase();
                    let apFy = Number(ap?.fiscalYear ?? ap?.FiscalYear);
                    let apNum = Number(ap?.periodNumber ?? ap?.PeriodNumber);
                    if (Number.isNaN(apFy) || Number.isNaN(apNum)) {
                      const apDStr = ap?.startDate ?? ap?.StartDate;
                      if (apDStr) {
                        const apD = new Date(apDStr);
                        if (!Number.isNaN(apD.getTime())) {
                          apFy = apD.getFullYear();
                          apNum = apD.getMonth() + 1;
                        }
                      }
                    }
                    const apId = String(ap?.fiscalPeriodID ?? ap?.FiscalPeriodID ?? '');
                    // For quarterly budgets, only include months in quarter that are also in date range
                    if (apType.includes('month') && apFy === fy && apNum >= startM && apNum <= endM && apId &&
                        allowedPeriodIds.has(apId)) {
                      expandedPeriodIds.add(apId);
                    }
                  }
                } else {
                  // Fallback to the given id
                  expandedPeriodIds.add(id);
                }
              } else if (budgetTypeNorm === 'annually') {
                // For annual budgets, include ALL monthly periods in the fiscal year that overlap with date range
                // Don't restrict by the original budget line period - that's just where it was created
                const budgetFiscalYear = !Number.isNaN(fy) ? fy : new Date().getFullYear();

                for (const ap of (this.allFiscalPeriods || [])) {
                  const apType = String(ap?.periodType ?? ap?.PeriodType ?? '').toLowerCase();
                  let apFy = Number(ap?.fiscalYear ?? ap?.FiscalYear);
                  if (Number.isNaN(apFy)) {
                    const apDStr = ap?.startDate ?? ap?.StartDate;
                    if (apDStr) {
                      const apD = new Date(apDStr);
                      if (!Number.isNaN(apD.getTime())) {
                        apFy = apD.getFullYear();
                      }
                    }
                  }
                  const apId = String(ap?.fiscalPeriodID ?? ap?.FiscalPeriodID ?? '');

                  // For annual budgets, include all months in the same fiscal year
                  // Only include periods that are also in the allowedPeriodIds (date range filter)
                  if (apType.includes('month') && apFy === budgetFiscalYear && apId &&
                      allowedPeriodIds.has(apId)) {
                    expandedPeriodIds.add(apId);
                  }
                }
              } else {
                // monthly / per-item: keep the specific month id
                expandedPeriodIds.add(id);
              }
            }

            // For actuals calculation, use the expanded monthly periods regardless of global filter
            const relevantPeriodIds = expandedPeriodIds;

            // Helpful maps for fallback matching
            const allowedIdsForBudget = new Set(relevantPeriodIds);
            const periodNumberToAllowedIds = new Map<number, string[]>();
            const yearsForAllowedIds = new Set<number>();
            for (const id of allowedIdsForBudget) {
              const fp = allPeriodsById.get(id);
              const pNum = Number(fp?.periodNumber ?? fp?.PeriodNumber);
              const y = Number(fp?.fiscalYear ?? fp?.FiscalYear);
              if (!Number.isNaN(pNum)) {
                const arr = periodNumberToAllowedIds.get(pNum) || [];
                arr.push(id);
                periodNumberToAllowedIds.set(pNum, arr);
              }
              if (!Number.isNaN(y)) yearsForAllowedIds.add(y);
            }

            let sum = 0;
            const includedRows: { period: string; amount: number }[] = [];
            const excludedRows: { period: string; amount: number }[] = [];
            if (relevantPeriodIds.size > 0) {
              const rel = relevantPeriodIds;
              // For quarterly/annual budgets, we aggregate all actuals in the period (ignore budgetID match)
              const matchByBudgetId = (budgetTypeNorm === 'monthly' || budgetTypeNorm === 'per-item');
              for (const entry of actualsNormalized) {
                if (matchByBudgetId && entry.budgetID !== bIdStr) continue;

                const periodStr = String(entry.period);
                const amt = entry.totalActualCostFromInventory || 0;
                let included = false;

                // 1) Exact fiscalPeriodID match within expanded months
                if (rel.has(periodStr)) {
                  included = true;
                } else {
                  const pNum = Number(periodStr);
                  // 2) If numeric, try month-number fallback
                  if (!Number.isNaN(pNum)) {
                    if (periodNumberToAllowedIds.has(pNum)) {
                      included = true;
                    } else if (
                      periodStr.length === 4 && pNum >= 1900 && pNum <= 3000
                    ) {
                      // 3) If it looks like a year, include if expanded months include that fiscal year
                      if (yearsForAllowedIds.has(pNum)) {
                        included = true;
                      }
                    }
                  }
                }

                if (included) {
                  sum += amt;
                  includedRows.push({ period: periodStr, amount: amt });
                } else {
                  // Only show excluded if entry belongs to this budget (to reduce noise) or we are aggregating across all budgets
                  if (!matchByBudgetId || entry.budgetID === bIdStr) {
                    excludedRows.push({ period: periodStr, amount: amt });
                  }
                }
              }
            }

            actualCostByBudgetId[bIdStr] = sum;

            debugMap[bIdStr] = {
              budgetID: bIdStr,
              budgetName: (budget as any).budgetName,
              linePeriodIds: Array.from(itemPeriodIds),
              expandedPeriodIds: Array.from(expandedPeriodIds),
              allowedPeriodIds: Array.from(allowedPeriodIds),
              included: includedRows,
              excluded: excludedRows,
              sum,
            };

            return {
              ...budget,
              createdAt:
                (budget as any).createdAt instanceof Date
                  ? ((budget as any).createdAt as Date).toISOString()
                  : (budget as any).createdAt,
              lineItems: items,
              totalActualCostFromInventory: sum,
            } as Budget;
          });

          this.actualCostByBudgetId = actualCostByBudgetId;
          this.actualsDebugByBudget = debugMap;

          // Compute deduped grand total for the metric card based on current fiscal period filter
          const allowedIdsForCard = new Set(
            (this.fiscalPeriods || []).map((p: any) => String(p?.fiscalPeriodID ?? p?.FiscalPeriodID))
          );
          const allowedYearsForCard = new Set<number>(
            (this.fiscalPeriods || []).map((p: any) => {
              const y = Number(p?.fiscalYear ?? p?.FiscalYear);
              if (!Number.isNaN(y)) return y;
              const dStr = p?.startDate ?? p?.StartDate;
              const d = dStr ? new Date(dStr) : null;
              return d && !Number.isNaN(d.getTime()) ? d.getFullYear() : undefined as any;
            }).filter((v: any) => typeof v === 'number')
          );
          const allowedMonthNumsForCard = new Set<number>(
            (this.fiscalPeriods || []).map((p: any) => {
              const m = Number(p?.periodNumber ?? p?.PeriodNumber);
              if (!Number.isNaN(m)) return m;
              const dStr = p?.startDate ?? p?.StartDate;
              const d = dStr ? new Date(dStr) : null;
              return d && !Number.isNaN(d.getTime()) ? (d.getMonth() + 1) : undefined as any;
            }).filter((v: any) => typeof v === 'number')
          );

          const cardSum = (actualsNormalized || []).reduce((sum: number, entry: any) => {
            const periodStr = String(entry.period);
            let include = false;
            if (allowedIdsForCard.has(periodStr)) {
              include = true;
            } else {
              const pNum = Number(periodStr);
              if (!Number.isNaN(pNum)) {
                // Year format
                if (periodStr.length === 4 && pNum >= 1900 && pNum <= 3000) {
                  include = allowedYearsForCard.has(pNum);
                } else if (pNum >= 1 && pNum <= 12) {
                  // Month-number-only; include if the month exists in the allowed set
                  // To avoid cross-year ambiguity, require exactly one allowed year if month-only
                  include = allowedMonthNumsForCard.has(pNum) && allowedYearsForCard.size <= 1;
                }
              }
            }
            return include ? (sum + (Number(entry.totalActualCostFromInventory) || 0)) : sum;
          }, 0);

          this.grandTotalActualCosts = cardSum;

          // Optional: keep a copy for debugging/inspection
          this.monthlyActualCosts = actualsNormalized.map((a) => ({
            budgetID: a.budgetID,
            budgetName: '',
            budgetCreationMonth: a.period,
            totalActualCostFromInventory: a.totalActualCostFromInventory,
          }));

          return combinedBudgets as Budget[];
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (combinedBudgets: Budget[]) => {
          this.budgets = combinedBudgets;
          this.refreshChartData();
        },
        error: (error) => {
          console.error('Error combining budget data:', error);
        },
      });
  }

  loadItems(): void {
    this.updateCharts();
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    this.http
      .get<ItemDTO[]>(`${environment.apiUrl}/api/Items/GetAll`, { headers })
      .pipe(timeout(15000))
      .subscribe({
        next: (items) => {
          this.items = items;
          this.itemtotal = items.length;
          this.itemsProducts = items.filter((item) => item.itemTypeID === 2);
// $&
        },
        complete: () => {
          // this.isProcessing = false;
        },
        error: (error) => {
          console.error('Error loading items:', error);
        },
      });
  }

  onBudgetAmountChange(value: string, index: number) {
    // Remove non-numeric characters and convert to number
    const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, ''));
    if (!isNaN(numericValue)) {
      this.budgetLineItems[index].budgetAmount = numericValue;
    }
  }

  switchYear(year: number): void {
    this.selectedYear = year;
    this.dateRange = {
      start: new Date(year, 0, 1),
      end: new Date(year, 11, 31),
    };
    this.loadFiscalPeriods(year);
    this.currentPage = 1; // reset pagination when switching year
    this.loadAllBudgetData();
    // this.refreshChartData() will be called by loadAllBudgetData on completion
  }

  onFiscalYearChange(year: number): void {
    this.budgetForm.fiscalYear = year;
    this.selectedYear = year;
    this.loadFiscalPeriods(year);
    this.currentPage = 1; // reset pagination on filter change
    this.loadAllBudgetData();
  }

  public calculateActualsFromInventoryByMonth(budget: any): number {
    let totalActualCostForBudgetMonth = 0;

    if (
      !budget ||
      !budget.createdAt ||
      !budget.lineItems ||
      !this.fiscalPeriods ||
      !this.inventoryTransactions
    ) {
      // Ensure necessary data is available
      console.warn('Missing data for calculateActualsFromInventoryByMonth', {
        budget,
        fiscalPeriods: this.fiscalPeriods,
        inventoryTransactions: this.inventoryTransactions,
      });
      return 0;
    }

    const budgetDate = new Date(budget.createdAt);
    const budgetCreationMonth = budgetDate.getMonth();
    const budgetCreationYear = budgetDate.getFullYear();

    let inventoryCalculatedForThisMonth = false;

    for (const lineItem of budget.lineItems) {
      if (inventoryCalculatedForThisMonth) {
        break; // Already processed inventory for this budget's specific month
      }

      if (lineItem.fiscalPeriodID) {
        // Assuming fiscalPeriodID exists on BudgetLineItem
        const fiscalPeriod = this.fiscalPeriods.find(
          (fp) => fp.fiscalPeriodID === lineItem.fiscalPeriodID
        );

        if (
          fiscalPeriod &&
          fiscalPeriod.periodType &&
          fiscalPeriod.periodType.toLowerCase() === 'month'
        ) {
          const fpStartDate = new Date(fiscalPeriod.startDate);
          const fiscalPeriodMonth = fpStartDate.getMonth();
          const fiscalPeriodYear = fpStartDate.getFullYear();

          // Check if the fiscal period's start date month/year matches the budget's creation month/year
          if (
            fiscalPeriodMonth === budgetCreationMonth &&
            fiscalPeriodYear === budgetCreationYear
          ) {
            // This budget line confirms the relevance of this month for the budget.
            // Now, sum all inventory transactions for this specific month and year.
            for (const transaction of this.inventoryTransactions) {
              const transDate = new Date(transaction.transactionDate);
              const transactionMonth = transDate.getMonth();
              const transactionYear = transDate.getFullYear();

              if (
                transactionMonth === budgetCreationMonth &&
                transactionYear === budgetCreationYear
              ) {
                totalActualCostForBudgetMonth +=
                  transaction.quantityChanged * transaction.costPerUnit;
              }
            }
            inventoryCalculatedForThisMonth = true; // Mark that we've done the sum for this budget's month
          }
        }
      }
    }
    return totalActualCostForBudgetMonth;
  }

  public calculateTotalLineItemAmount(budget: Budget): number {
    if (!budget || !budget.lineItems || !Array.isArray(budget.lineItems)) {
      return 0;
    }

    const total = budget.lineItems.reduce(
      (sum: number, item: BudgetLineItem) => {
        const amount = Number(item.budgetAmount);
        return sum + (isNaN(amount) ? 0 : amount);
      },
      0
    );

    return total;
  }
  public calculateGrandTotalBudgetAmount(): number {
    let grandTotal = 0;
    if (this.budgets && this.budgets.length > 0) {
      for (const budget of this.budgets) {
        // This uses the method to sum line items for a single budget
        grandTotal += this.calculateTotalLineItemAmount(budget);
      }
    }
    return grandTotal;
  }

  // getBudgetVariance(budget: Budget): number {
  //   return (budget.actualAmount || 0) - (budget.plannedAmount || 0);
  // }
  getVarianceStatus(variance: number): string {
    if (variance > 0) return 'Over Budget';
    if (variance < 0) return 'Under Budget';
    return 'On Budget';
  }

  getVarianceClass(variance: number): string {
    if (variance > 0) return 'text-danger';
    if (variance < 0) return 'text-success';
    return 'text-muted';
  }

  loadBudgetAssignments(orderId: number): void {
    this.inventoryService.getBudgetAssignments(orderId).subscribe({
      next: (transactions: any[]) => {
        this.budgetAssignments = transactions.map((t: any) => ({
          budgetId: Number(t.Notes),
          amount: t.CostPerUnit,
          date: t.TransactionDate
        }));
        this.compareCostsToBudgets();
      },
      error: (err) => console.error('Failed to load assignments', err)
    });
  }

  compareCostsToBudgets(): void {
    this.budgetAssignments.forEach(assignment => {
      const budget = this.budgets.find(b => b.budgetID === assignment.budgetId.toString());
      if (budget) {
        const actualCost = this.getActualCostForBudget(budget.budgetID);
        const assignedAmount = assignment.amount;
        const variance = actualCost - assignedAmount;
      }
    });
  }

  getActualCostForBudget(budgetId: string): number {
    // First try to get from the pre-calculated actualCostByBudgetId
    const key = String(budgetId);
    const preCalculated = this.actualCostByBudgetId?.[key];

    if (preCalculated !== undefined) {
      // For debugging
      if (key === '14') {
        const debugInfo = this.actualsDebugByBudget[key];
        if (debugInfo) {
        }
      }
      return preCalculated;
    }

    // Fallback to calculate from monthlyActualCosts if available
    if (this.monthlyActualCosts && this.monthlyActualCosts.length > 0) {
      const monthlySum = this.monthlyActualCosts
        .filter(actual => actual.budgetID === budgetId)
        .reduce((sum, actual) => sum + (actual.totalActualCostFromInventory || 0), 0);

      // For debugging
      if (key === '14') {
      }

      return monthlySum;
    }

    return 0;
  }
}
